/**
 * Admin User Provisioning Page
 *
 * Admin-only interface for creating new users without email confirmation.
 * Creates Cognito user with temporary password and all required database records.
 *
 * Security: Requires rp_admin global role
 * Rate limited: 5 burst, 2/sec sustained
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Divider,
  Radio,
  Select,
  Alert,
  Result,
  Descriptions,
  Tag,
  message,
} from 'antd'
import {
  UserAddOutlined,
  MailOutlined,
  LockOutlined,
  UserOutlined,
  PhoneOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

import { adminService } from '@/services/adminService'
import { errorService } from '@/services/errorService'
import type {
  ProvisionUserRequest,
  ProvisionUserResponse,
  ProvisionUserResponseData,
  OrgMode,
} from '@/types/admin.types'

const { Title, Text, Paragraph } = Typography

/**
 * Password validation rules matching backend requirements
 */
const passwordRules = [
  { required: true, message: 'Temporary password is required' },
  { min: 12, message: 'Password must be at least 12 characters' },
  {
    pattern: /[A-Z]/,
    message: 'Password must contain at least one uppercase letter',
  },
  {
    pattern: /[a-z]/,
    message: 'Password must contain at least one lowercase letter',
  },
  {
    pattern: /[0-9]/,
    message: 'Password must contain at least one digit',
  },
  {
    pattern: /[!@#$%^&*(),.?":{}|<>]/,
    message: 'Password must contain at least one special character',
  },
]

/**
 * Email validation regex
 */
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function ProvisionUserPage() {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProvisionUserResponse | null>(null)
  const [orgMode, setOrgMode] = useState<OrgMode>('new')

  // Fetch organizations for existing org dropdown
  const { data: orgsData, isLoading: orgsLoading } = useQuery({
    queryKey: ['admin-organizations'],
    queryFn: async () => {
      const response = await adminService.getOrganizations()
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch organizations')
      }
      return response.data || []
    },
    enabled: orgMode === 'existing', // Only fetch when needed
  })

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: any) => {
    setSubmitting(true)
    setResult(null)

    try {
      // Build request based on org mode
      const request: ProvisionUserRequest = {
        email: values.email.trim().toLowerCase(),
        temporaryPassword: values.temporaryPassword,
        userName: values.userName?.trim() || undefined,
        phoneNumber: values.phoneNumber?.trim() || undefined,
        orgMode: values.orgMode,
      }

      if (values.orgMode === 'new') {
        request.orgName = values.orgName?.trim()
      } else {
        request.orgId = values.orgId
        request.membershipRoles = values.membershipRoles
      }

      const response = await adminService.provisionUser(request)

      if (!response.success) {
        errorService.showError(
          'User Provisioning Failed',
          response.message || 'Failed to provision user',
          response.errors
        )
        return
      }

      // Store result for success display
      // apiService unwraps body.data, so response.data is ProvisionUserResponseData
      // We need to reconstruct the full response structure for display
      setResult({
        data: response.data as ProvisionUserResponseData,
        meta: (response.meta || {}) as ProvisionUserResponse['meta'],
        errors: response.errors || [],
      })

      // Check for partial failure
      if (response.meta?.status === 'PARTIAL_FAILURE') {
        message.warning('User created with partial failure - see details below')
      } else {
        message.success('User provisioned successfully')
      }
    } catch (error: any) {
      errorService.showError(
        'Provisioning Error',
        'An unexpected error occurred while provisioning the user',
        [{ code: 'UNKNOWN_ERROR', message: error?.message || String(error) }]
      )
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Reset form to provision another user
   */
  const handleReset = () => {
    form.resetFields()
    setResult(null)
    setOrgMode('new')
  }

  /**
   * Handle org mode change
   */
  const handleOrgModeChange = (value: OrgMode) => {
    setOrgMode(value)
    // Clear conditional fields when switching modes
    if (value === 'new') {
      form.setFieldsValue({ orgId: undefined, membershipRoles: undefined })
    } else {
      form.setFieldsValue({ orgName: undefined })
    }
  }

  // Show success result if we have one
  if (result) {
    const isPartialFailure = result.meta?.status === 'PARTIAL_FAILURE'

    return (
      <Card style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Result
          status={isPartialFailure ? 'warning' : 'success'}
          icon={isPartialFailure ? <WarningOutlined /> : <CheckCircleOutlined />}
          title={isPartialFailure ? 'User Created with Issues' : 'User Provisioned Successfully'}
          subTitle={
            isPartialFailure
              ? 'The user was created but some resources failed. Manual intervention required.'
              : 'The user can now log in with the temporary password and will be prompted to change it.'
          }
          extra={[
            <Button
              key="another"
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleReset}
            >
              Provision Another User
            </Button>,
            <Button key="back" onClick={() => navigate('/admin/audit-logs')}>
              View Audit Logs
            </Button>,
          ]}
        >
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="User">
              <Space>
                <Text copyable={{ text: result.data.user.uid }}>
                  {result.data.user.email}
                </Text>
                <Tag color="blue">{result.data.user.userName}</Tag>
              </Space>
            </Descriptions.Item>

            {result.data.subscription && (
              <Descriptions.Item label="Subscription">
                <Space>
                  <Tag color="green">{result.data.subscription.planTier}</Tag>
                  <Tag>{result.data.subscription.status}</Tag>
                </Space>
              </Descriptions.Item>
            )}

            {result.data.organization && (
              <Descriptions.Item label="Organization">
                <Space>
                  <Text copyable={{ text: result.data.organization.uid }}>
                    {result.data.organization.orgName}
                  </Text>
                  {result.data.organization.isNew && <Tag color="purple">New</Tag>}
                </Space>
              </Descriptions.Item>
            )}

            {result.data.membership && (
              <Descriptions.Item label="Membership Roles">
                {result.data.membership.roles.map(role => (
                  <Tag key={role} color="gold">{role}</Tag>
                ))}
              </Descriptions.Item>
            )}
          </Descriptions>

          {isPartialFailure && result.errors && result.errors.length > 0 && (
            <Alert
              type="warning"
              style={{ marginTop: 16 }}
              message="Action Required"
              description={
                <>
                  {result.errors.map((error, idx) => (
                    <Paragraph key={idx}>
                      <Text strong>{error.code}:</Text> {error.message}
                      {error.remediation && (
                        <Text type="secondary"> - {error.remediation}</Text>
                      )}
                    </Paragraph>
                  ))}
                </>
              }
            />
          )}
        </Result>
      </Card>
    )
  }

  return (
    <Card
      style={{ maxWidth: '700px', margin: '0 auto' }}
      title={
        <Space>
          <UserAddOutlined />
          <Title level={3} style={{ margin: 0 }}>Provision New User</Title>
        </Space>
      }
    >
      <Alert
        type="info"
        style={{ marginBottom: 24 }}
        message="Admin User Provisioning"
        description="Create a new user without email confirmation. The user will be required to change their password on first login."
        showIcon
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          orgMode: 'new',
          membershipRoles: ['viewer'],
        }}
      >
        {/* Email */}
        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { pattern: emailPattern, message: 'Please enter a valid email address' },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="user@example.com"
            size="large"
            autoComplete="off"
          />
        </Form.Item>

        {/* Temporary Password */}
        <Form.Item
          label="Temporary Password"
          name="temporaryPassword"
          rules={passwordRules}
          extra="User will be required to change this on first login"
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Minimum 12 characters with mixed case, numbers, and symbols"
            size="large"
            autoComplete="new-password"
          />
        </Form.Item>

        <Divider>User Details (Optional)</Divider>

        {/* Display Name */}
        <Form.Item
          label="Display Name"
          name="userName"
          rules={[{ max: 255, message: 'Display name must be less than 255 characters' }]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="John Doe"
            size="large"
          />
        </Form.Item>

        {/* Phone Number */}
        <Form.Item
          label="Phone Number"
          name="phoneNumber"
          rules={[
            { max: 20, message: 'Phone number must be less than 20 characters' },
            {
              pattern: /^\+?[0-9\s\-()]+$/,
              message: 'Please enter a valid phone number',
            },
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="+1 (555) 123-4567"
            size="large"
          />
        </Form.Item>

        <Divider>Organization Setup</Divider>

        {/* Organization Mode */}
        <Form.Item
          label="Organization Mode"
          name="orgMode"
          rules={[{ required: true, message: 'Please select organization mode' }]}
        >
          <Radio.Group
            onChange={(e) => handleOrgModeChange(e.target.value)}
            buttonStyle="solid"
            size="large"
          >
            <Radio.Button value="new">
              <Space>
                <TeamOutlined />
                Create New Organization
              </Space>
            </Radio.Button>
            <Radio.Button value="existing">
              <Space>
                <TeamOutlined />
                Join Existing Organization
              </Space>
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* New Org: Organization Name */}
        {orgMode === 'new' && (
          <Form.Item
            label="Organization Name"
            name="orgName"
            rules={[
              { required: true, message: 'Organization name is required for new organizations' },
              { max: 255, message: 'Organization name must be less than 255 characters' },
            ]}
          >
            <Input
              prefix={<TeamOutlined />}
              placeholder="Acme Corporation"
              size="large"
            />
          </Form.Item>
        )}

        {/* Existing Org: Organization Dropdown */}
        {orgMode === 'existing' && (
          <>
            <Form.Item
              label="Select Organization"
              name="orgId"
              rules={[
                { required: true, message: 'Please select an organization' },
              ]}
            >
              <Select
                showSearch
                size="large"
                placeholder="Search and select an organization..."
                loading={orgsLoading}
                optionFilterProp="label"
                options={orgsData?.map(org => ({
                  value: org.uid,
                  label: org.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Membership Roles"
              name="membershipRoles"
              rules={[
                { required: true, message: 'Please select at least one role' },
              ]}
            >
              <Select
                mode="multiple"
                size="large"
                placeholder="Select roles for the user..."
                options={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'sales', label: 'Sales' },
                  { value: 'pricer', label: 'Pricer' },
                  { value: 'datamgr', label: 'Data Manager' },
                  { value: 'viewer', label: 'Viewer' },
                ]}
              />
            </Form.Item>
          </>
        )}

        <Divider />

        {/* Submit Button */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              size="large"
              icon={<UserAddOutlined />}
            >
              Provision User
            </Button>
            <Button
              htmlType="button"
              onClick={handleReset}
              size="large"
              icon={<ReloadOutlined />}
            >
              Reset Form
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}
